import { TreeAdapter, TreeAdapterParams, TreeOptions, TreeSource } from "@omegagrid/tree";
import { Service } from "@reporting/rp-app-host/src/client";
import { ETLJob } from "@reporting/rp-types/jobs";

/** Fetches the list of a job parameter; the adapter keeps the result. */
type ListLoader = () => Promise<TreeSource>;

export class ParamTreeAdapter extends TreeAdapter {

	private items: TreeSource;
	private loading: Promise<TreeSource>;

	constructor(private loader: ListLoader) {
		super();
	}

	/** True once the list has been fetched - getLabel() knows the labels only then. */
	get loaded() {
		return this.items != null;
	}

	getTree(_params?: TreeAdapterParams): Promise<TreeSource> {
		if (this.items) return Promise.resolve(this.items);
		// the dropdown and the label lookup may ask at the same time - one request serves both;
		// a failed request is not cached so the next call retries
		if (!this.loading) this.loading = this.load().finally(() => this.loading = null);
		return this.loading;
	}

	private async load(): Promise<TreeSource> {
		return this.items = (await this.loader()) ?? [];
	}

	/** Label of the list item with the given key; null until the list is loaded or when the key is not in it. */
	getLabel(key: string|number): string|null {
		const node = this.items?.find(item => String(item.key ?? item.k) === String(key));
		return node ? (node.value ?? node.v ?? null) : null;
	}

	getOptions(): Partial<TreeOptions> {
		return {
			autoClose: true,
			selectMode: 'single'
		};
	}

}

/** The services the lists come from: a list thread runs in etl, the lists of a default job come from jobs. */
export type ParamListServices = { etl: Service, jobs: Service };

/**
 * True when the parameter takes its values from a list: the list thread of the job (id_thread_*), or - for a default
 * job, which has no threads - the list the jobs service keeps for every described parameter (description_*).
 */
export const hasParamList = (job: ETLJob, param: string) =>
	job.default_flag ? !!job[`description_${param}` as keyof ETLJob] : !!job[`id_thread_${param}` as keyof ETLJob];

const loadThreadList = (etl: Service, idThread: number): ListLoader => async () => {
	const result = await etl.get<TreeSource[]>(`/exec/${idThread}`, { scope: 1 }, { headers: { accept: 'application/json' } });
	return result && result.length ? result[0] : [];
};

const loadDefaultJobList = (jobs: Service, job: ETLJob, param: string): ListLoader => () =>
	jobs.get<TreeSource>(`defaultJobs/${job.job_name}/params/${param}`);

/** The adapters per company (the current one, see getParamTreeAdapter) and list. */
export const paramTreeAdapters = new Map<number, Map<string, ParamTreeAdapter>>();

/**
 * The lists are fetched in the current company (the services take it from the accounting provider), so the adapter
 * is cached under it - not under job.accounting: the default jobs carry none, so the lists of the first company would
 * be served in every other one, and a job shared through entity access carries the company it was defined in.
 */
export const getParamTreeAdapter = (services: ParamListServices, job: ETLJob, param: string) => {
	const accounting = services.jobs.a ?? services.etl.a ?? job.accounting;
	const idThread = job[`id_thread_${param}` as keyof ETLJob] as number;
	const key = job.default_flag ? `${job.job_name}/${param}` : `thread/${idThread}`;
	if (!paramTreeAdapters.has(accounting)) paramTreeAdapters.set(accounting, new Map());
	const map = paramTreeAdapters.get(accounting);
	if (!map.has(key)) map.set(key, new ParamTreeAdapter(job.default_flag
		? loadDefaultJobList(services.jobs, job, param)
		: loadThreadList(services.etl, idThread)
	));
	return map.get(key);
}
